// packages/chatbot/src/components/ChatLayout.tsx

import { IconButton, SplitScreenLayout } from '@dopomogai/ui';
import { PanelLeftOpen, PanelRightOpen, PlusCircle, X, Maximize2 } from 'lucide-react';
import type { ChatLayoutProps as PublicChatLayoutProps, ToolUiConfig } from '../types';
import { Sidebar } from './Sidebar';
import { ChatWindow } from './ChatWindow';
import { AgentSelector } from './AgentSelector';
import { ChatBrandFooter } from './ChatBrandFooter';
import { TabsBar } from './TabsBar';

interface ChatLayoutProps extends PublicChatLayoutProps {
  isSidebarVisible: boolean;
  onToggleSidebar: () => void;
  headerTitle?: string;
  showBrandFooter?: boolean;
  brandSlot?: React.ReactNode;
  brandHref?: string;
  onReplayFrontendActions?: (toolCallId: string) => void;
  toolUi?: ToolUiConfig;
  conversationKey?: string;
}

export function ChatLayout(props: ChatLayoutProps) {
  const chatTitle = props.headerTitle || 'AI Concierge';

  const frameClass =
    props.frameStyle === 'app'
      ? 'w-full h-full bg-white'
      : 'w-full h-full border-2 border-black rounded-xl bg-white shadow-offset-black overflow-hidden';

  const header = (
    <div
      className="flex-shrink-0 flex items-center justify-between gap-2 p-3 border-b-2 border-black"
      style={{ backgroundColor: '#E0F2FE', ...props.headerStyle }}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Always show toggle so users can close the sidebar when it's visible */}
        {props.mode === 'full' && (
          <IconButton
            icon={props.isSidebarVisible ? <PanelRightOpen /> : <PanelLeftOpen />}
            onClick={props.onToggleSidebar}
            ariaLabel={props.isSidebarVisible ? 'Hide sidebar' : 'Show sidebar'}
            variant="ghost"
          />
        )}
        <h3 className="font-bold text-slate-800 truncate" title={chatTitle}>
          {chatTitle}
        </h3>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        {props.showAgentSelector && props.agents && props.activeAgentId && props.onSelectAgent && (
          <AgentSelector
            agents={props.agents}
            activeAgentId={props.activeAgentId}
            onSelectAgent={props.onSelectAgent}
          />
        )}
        {props.mode === 'full' && (
          <IconButton
            icon={<PlusCircle className="w-5 h-5" />}
            onClick={() => props.onSelectConversation(null)}
            ariaLabel="Start new chat"
            variant="ghost"
          />
        )}
        {props.onMaximize && (
          <IconButton
            icon={<Maximize2 />}
            onClick={props.onMaximize}
            ariaLabel="Open fullscreen"
            variant="ghost"
          />
        )}
        {props.onClose && props.closable !== false && (
          <IconButton icon={<X />} onClick={props.onClose} ariaLabel="Close chat" variant="ghost" />
        )}
      </div>
    </div>
  );

  const chatPane = (
    <div className="flex flex-col bg-white h-full min-w-0 min-h-0">
      {header}

      {props.mode === 'full' && props.historyView === 'tabs' && (
        <TabsBar
          conversations={props.conversations}
          activeConversationId={props.activeConversationId}
          onSelectConversation={props.onSelectConversation}
          orderKey={`dopo:chat:tabOrder:${props.activeAgentId || 'global'}`}
        />
      )}

      <ChatWindow
        messages={props.messages || []}
        assistantState={props.assistantState || 'idle'}
        activeToolCall={props.activeToolCall || null}
        onSendMessage={props.onSendMessage}
        onSubmitToolResponse={props.onSubmitToolResponse}
        onCancelTool={props.onCancelTool}
        toolRegistry={props.toolRegistry}
        isLoading={props.isLoadingMessages}
        error={props.messageError?.message || null}
        inputStyle={props.inputStyle}
        userAvatar={props.userAvatar}
        agentAvatar={props.agentAvatar}
        userMessageStyle={props.userMessageStyle}
        assistantMessageStyle={props.assistantMessageStyle}
        primaryColor={props.primaryColor}
        initialPrompts={props.initialPrompts}
        initialMessage={props.initialMessage}
        onReplayFrontendActions={props.onReplayFrontendActions}
        toolUi={props.toolUi}
        conversationKey={props.conversationKey}
      />
      {props.showBrandFooter !== false && (
        <ChatBrandFooter brandSlot={props.brandSlot} brandHref={props.brandHref} />
      )}
    </div>
  );

  if (props.mode !== 'full' || !props.isSidebarVisible) {
    return (
      <div className={frameClass}>
        <div className="flex h-full w-full min-w-0 min-h-0 relative overflow-hidden">
          <div className="flex-1 min-w-0 min-h-0">{chatPane}</div>
          {props.customUIs}
        </div>
      </div>
    );
  }

  return (
    <div className={frameClass}>
      <div className="flex h-full w-full min-w-0 min-h-0 relative overflow-hidden">
        <div className="flex-1 min-w-0 min-h-0">
          <SplitScreenLayout
            orientation="horizontal"
            initialSizes={[66, 34]}
            minPaneSize={280}
            className="h-full w-full"
          >
            <Sidebar
              conversations={props.conversations}
              activeConversationId={props.activeConversationId}
              onSelectConversation={props.onSelectConversation}
              isLoading={props.isConversationsLoading}
              isMoreConversationsLoading={props.isMoreConversationsLoading}
              hasMoreConversations={props.hasMoreConversations}
              onLoadMoreConversations={props.onLoadMoreConversations}
              primaryColor={props.primaryColor}
              hideBranding
              onCloseSidebar={props.onToggleSidebar}
            />
            {chatPane}
          </SplitScreenLayout>
        </div>
        {props.customUIs}
      </div>
    </div>
  );
}
